import { VideoResolution } from '@/ai/video-models';
import { getTrendCredits, TRENDS } from '@/config/trends';
import type { CreditPackage } from '@/payment/types';

const HOME_TREND = TRENDS['crazy-frog'];

/** "Two Crazy Frog AI videos at 480p, or one in 720p HD." */
function describePack(credits: number) {
  const sd = Math.floor(
    credits / getTrendCredits(HOME_TREND, VideoResolution.P480)
  );
  const hd = Math.floor(
    credits / getTrendCredits(HOME_TREND, VideoResolution.P720)
  );
  const videos = (count: number) =>
    `${count} ${HOME_TREND.name} ${count === 1 ? 'video' : 'videos'}`;
  return hd > 0
    ? `${videos(sd)} at 480p, or ${hd} in 720p HD.`
    : `${videos(sd)} at 480p.`;
}

export type PricingMode = 'one-time' | 'monthly' | 'yearly';

export type PricingFeature = {
  text: string;
  emphasis?: boolean;
};

export type PricingPlan = {
  id: string;
  packageId?: string;
  name: string;
  price: number;
  priceYearly?: number;
  priceYearlyTotal?: string;
  creditAmount: number;
  costPerCredit?: string;
  costPerCreditYearly?: string;
  credits: string;
  creditsYearly?: string;
  description: string;
  features: PricingFeature[];
  isPopular?: boolean;
};

/**
 * Legacy lifetime price in cents. The plan stays in billing config for
 * people who already bought it, and it is not offered for sale.
 */
export const LIFETIME_PRICE_CENTS = 19900;

/** Anchor clip: 15s Seedance 2.5 at 480p. */
export const PRICING_VIDEO_CREDIT_COST = 50;
/** Cheapest current image charge under the new credit unit. */
export const PRICING_IMAGE_CREDIT_COST = 1;

export function calculatePricingOutputLimits(credits: number) {
  return {
    images: Math.floor(credits / PRICING_IMAGE_CREDIT_COST),
    videos: Math.floor(credits / PRICING_VIDEO_CREDIT_COST),
  };
}

/**
 * Kept so existing Stripe subscriptions can still be recognized. Not shown
 * on the pricing page and not granted in the new credit unit.
 */
export const legacySubscriptionPlans: PricingPlan[] = [
  {
    id: 'starter',
    name: 'STARTER',
    price: 9.9,
    priceYearly: 7.9,
    priceYearlyTotal: '$94.80',
    creditAmount: 1000,
    costPerCredit: '$0.99 / 100 credits',
    costPerCreditYearly: '$0.79 / 100 credits',
    credits: '1,000 credits/month',
    creditsYearly: '12,000 credits/year',
    description: 'Up to 40 short videos or 50 AI images each month',
    features: [
      { text: 'Every video tool, from text to video to upscaling' },
      { text: 'Image generation, editing, and image-to-video' },
      { text: 'Standard queue priority' },
      { text: 'Commercial use for generated outputs' },
    ],
  },
  {
    id: 'growth',
    name: 'GROWTH',
    price: 29.9,
    priceYearly: 23.9,
    priceYearlyTotal: '$286.80',
    creditAmount: 5000,
    costPerCredit: '$0.60 / 100 credits',
    costPerCreditYearly: '$0.48 / 100 credits',
    credits: '5,000 credits/month',
    creditsYearly: '60,000 credits/year',
    description: 'Up to 200 short videos or 250 AI images each month',
    features: [
      { text: 'Every video tool, from text to video to upscaling' },
      { text: 'Higher-volume creative testing' },
      { text: 'Priority queue access' },
      { text: 'Commercial use for generated outputs' },
      { text: 'Batch-ready campaign iteration' },
    ],
    isPopular: true,
  },
  {
    id: 'pro',
    name: 'PRO',
    price: 59.9,
    priceYearly: 47.9,
    priceYearlyTotal: '$574.80',
    creditAmount: 11_000,
    costPerCredit: '$0.54 / 100 credits',
    costPerCreditYearly: '$0.44 / 100 credits',
    credits: '11,000 credits/month',
    creditsYearly: '132,000 credits/year',
    description: 'Up to 440 short videos or 550 AI images each month',
    features: [
      { text: 'Every video tool, from text to video to upscaling' },
      { text: 'Best value for studio-style production' },
      { text: 'Highest queue priority' },
      { text: 'Commercial use for generated outputs' },
      { text: 'Large campaign and agency workflows' },
    ],
  },
];

/** Public catalog. Subscriptions are no longer sold. */
export const subscriptionPlans: PricingPlan[] = [];

const sharedPackFeatures: PricingFeature[] = [
  { text: 'Credits never expire' },
  { text: 'Failed jobs are refunded' },
];

export const oneTimePlans: PricingPlan[] = [
  {
    id: 'starter-drop',
    packageId: 'starter-drop',
    name: 'Starter Drop',
    price: 9.9,
    creditAmount: 50,
    credits: '50 credits',
    description: describePack(50),
    features: [
      { text: 'Try the format and ship your first clips.', emphasis: true },
      ...sharedPackFeatures,
    ],
  },
  {
    id: 'creator-stack',
    packageId: 'creator-stack',
    name: 'Creator Stack',
    price: 19.9,
    creditAmount: 105,
    credits: '105 credits',
    description: describePack(105),
    features: [
      { text: 'The best balance for regular creators.', emphasis: true },
      ...sharedPackFeatures,
    ],
    isPopular: true,
  },
  {
    id: 'studio-vault',
    packageId: 'studio-vault',
    name: 'Studio Vault',
    price: 29.9,
    creditAmount: 160,
    credits: '160 credits',
    description: describePack(160),
    features: [
      { text: 'Maximum value for a full content run.', emphasis: true },
      ...sharedPackFeatures,
    ],
  },
];

export const pricingFaqs = [
  {
    question: 'How does Crazy Frog AI pricing work?',
    answer:
      'Crazy Frog AI uses credits. Buy a one-time pack, then spend credits on videos and images. The exact cost is shown before you confirm. Credits never expire.',
  },
  {
    question: 'What happens if a video fails?',
    answer:
      'If a generation fails or is blocked by the AI provider, the credits for that job are returned to your balance automatically. You are only charged for jobs that finish.',
  },
  {
    question: 'Do I need a subscription?',
    answer:
      'No. Credit packs are a one-time purchase. The same balance works across every video and image tool on the site.',
  },
  {
    question: 'How many credits does a video use?',
    answer: `A ${HOME_TREND.templateSeconds}-second ${HOME_TREND.name} video uses ${getTrendCredits(HOME_TREND, VideoResolution.P480)} credits at 480p or ${getTrendCredits(HOME_TREND, VideoResolution.P720)} credits at 720p HD. Other tools show their credit cost on the Generate button before you confirm.`,
  },
  {
    question: 'Do new accounts get free credits?',
    answer:
      'No. Every video is a real AI render, so generating starts with a credit pack. The Starter Drop covers one Crazy Frog AI video at 480p.',
  },
  {
    question: 'Can I use Crazy Frog AI videos commercially?',
    answer:
      'Paid credit packs include commercial use of the videos and images you generate. Only upload content you own or have permission to use, label clips as AI-generated where a platform requires it, and remember that you are responsible for how you use the final output.',
  },
];

/** Schema.org offer for tools paid with credit packs (no free tier). */
export function getCreditPackSchemaOffer(
  description = 'Paid with credits from one-time credit packs.'
) {
  const prices = oneTimePlans.map((plan) => plan.price);
  return {
    '@type': 'AggregateOffer',
    priceCurrency: 'USD',
    lowPrice: Math.min(...prices).toFixed(2),
    highPrice: Math.max(...prices).toFixed(2),
    offerCount: prices.length,
    availability: 'https://schema.org/InStock',
    description,
  };
}

export function getPricingPlanGroups() {
  return {
    subscriptions: subscriptionPlans,
    oneTime: oneTimePlans,
  };
}

export function getStarterCreditPackage(): CreditPackage | undefined {
  return getCreditPackages().find((pkg) => pkg.id === 'starter-drop');
}

export function getPricingFaqs(): { question: string; answer: string }[] {
  return pricingFaqs;
}

export function getCreditPackages(): CreditPackage[] {
  return oneTimePlans.map((plan) => ({
    id: plan.packageId ?? plan.id,
    amount: plan.creditAmount,
    popular: Boolean(plan.isPopular),
    price: {
      priceId: '',
      amount: Math.round(plan.price * 100),
      currency: 'USD',
      allowPromotionCode: true,
    },
    tier: plan.isPopular ? 'pro' : 'starter',
    name: plan.name,
    description: plan.description,
    tagline: plan.costPerCredit,
    creditsValueDescription: plan.credits,
    features: plan.features.map((feature) => feature.text),
    ctaLabel: 'Buy credits',
  }));
}
